import dayjs from 'dayjs';
import { APIClient } from "../helpers/api_helper";

const api = new APIClient();

// The one save path for an existing intent. IntentDrawer runs it, and so does
// inline cell editing in IntentsTable -- an inline edit is exactly "open the
// drawer, change that one field, press Shrani": same endpoints, same payload,
// same confirmation steps. Keep it that way rather than growing a thinner
// single-field PATCH: the backend's update pipeline (amount reconciliation,
// transfer legs, split groups, repeats, DDV) is tuned to what the drawer sends.

export const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
export const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

const DATE_FIELDS = ['date_invoice_recieved', 'date_service_performed', 'date_to_be_paid', 'repeat_to_date'];
const AMOUNT_FIELDS = ['amount', 'amount_no_tax', 'tax_rate', 'tax_amount'];

const toDayjs = (v) => (v ? dayjs(v) : null);
const fmtDate = (d) => (d ? d.format('YYYY-MM-DD') : null);

/** A server row with its dates as dayjs, the shape the drawer's form holds. */
export const intentWithDayjsDates = (resp) => ({
  ...resp,
  ...Object.fromEntries(DATE_FIELDS.map(f => [f, toDayjs(resp[f])])),
});

/** The split editor's project list, rebuilt from the row's siblings. */
export const splitsOfIntent = (resp) =>
  resp.split_siblings.map(s => ({ project: s.project, split_percent: s.split_percent }));

/** The amounts a split row shows in the given edit mode: the master's total, or the child's share. */
export const splitModeAmounts = (mode, resp) => {
  const src = mode === 'master' ? resp.split_master : resp;
  return {
    amount: src.amount,
    amount_no_tax: src.amount_no_tax,
    tax_rate: src.tax_rate,
    tax_amount: src.tax_amount,
  };
};

const isSplitRow = (intent) => Array.isArray(intent?.split_siblings) && intent.split_siblings.length > 0;

/** The dimension the drawer opens an existing intent in: split, then repeat. */
export const defaultEditDimension = (intent) =>
  isSplitRow(intent) ? 'split' : (intent?.wizard ? 'wizard' : null);

/**
 * The form values the drawer holds right after loading `resp` (its load effect
 * plus applyMode('master')), before the user touches anything.
 */
export const formValuesFromIntent = (resp) => {
  const values = intentWithDayjsDates(resp);
  if (isSplitRow(resp)) {
    Object.assign(values, { project: null, project_splits: splitsOfIntent(resp) });
    if (resp.split_master) Object.assign(values, splitModeAmounts('master', resp));
  }
  return values;
};

/**
 * The fields the drawer's form has registered for this intent -- exactly what
 * its validateFields() returns, and so what a drawer save sends. Mirrors the
 * Form.Items in IntentDrawer.js (type + amount block), IntentFields/
 * DefaultFields.js / PersonalFields.js (+ TagField), TransferFields.js and
 * TaxFields.js. Keep in step with those when a Form.Item is added or made
 * conditional.
 */
export const drawerFieldNames = (intent, orgConfig) => {
  const type = intent.type;
  const names = ['type'];

  if (!orgConfig.tax_enabled) {
    names.push('amount');
  } else {
    names.push('amount_no_tax', 'amount', 'tax_rate');
    // Only where the rate is editable (!taxRateReadOnly; split rows open in master mode).
    if (!['TAX', 'TRANSFER'].includes(type)) names.push('tax_amount');
  }
  if (intent.tax_amount && type !== 'TAX') names.push('tax_intent_title');

  if (type === 'TRANSFER') {
    names.push('transfer_from_account', 'transfer_to_account', 'date_to_be_paid', 'is_paid', 'is_issued', 'title');
  } else if (type === 'TAX') {
    names.push('date_to_be_paid', 'is_paid', 'is_issued');
  } else if (['INCOME', 'EXPENSE'].includes(type)) {
    if (orgConfig.single_date) {
      names.push('date_to_be_paid', 'is_paid', 'title', 'descr', 'account', 'tag',
                 'company', 'person', 'category', 'project');
    } else {
      names.push(type === 'EXPENSE' ? 'date_invoice_recieved' : 'date_service_performed');
      names.push('date_to_be_paid', 'is_paid', 'is_issued', 'title', 'descr', 'account', 'tag',
                 'company', 'person', 'category');
      // The project select gives way to the "split between N projects" box.
      if (!isSplitRow(intent)) names.push('project');
      names.push('project_splits', 'invoice_number');
    }
    names.push('repeat_period');
    if (intent.repeat_period) names.push('repeat_to_date');
  }
  return names;
};

/**
 * The request body for saving drawer-form `values` -- dates as YYYY-MM-DD,
 * cleared fields as explicit nulls, personal orgs' VAT zeroed, split
 * bookkeeping stripped. Moved verbatim out of IntentDrawer.handleConfirmedSave.
 */
export const buildSavePayload = (values, { orgConfig, intentData, isEdit, wizardChoice, budgetSyncMode, splitChoice }) => {
    // Convert undefined → null so JSON.stringify doesn't silently drop cleared fields (e.g. tag, company)
    const sanitized = Object.fromEntries(Object.entries(values).map(([k, v]) => [k, v === undefined ? null : v]));
    const payload = {
        ...sanitized,
        wizard_choice: wizardChoice,
        ...(budgetSyncMode && { budget_sync_mode: budgetSyncMode }),
        ...(splitChoice && { split_choice: splitChoice }),
        date_invoice_recieved: fmtDate(sanitized.date_invoice_recieved),
        date_service_performed: fmtDate(sanitized.date_service_performed),
        date_to_be_paid: fmtDate(sanitized.date_to_be_paid),
        repeat_to_date: fmtDate(sanitized.repeat_to_date),
        // Personal orgs have no VAT. Sent explicitly rather than omitted:
        // set_amounts() on the server raises when tax_rate is null.
        ...(!orgConfig.tax_enabled && {
          tax_rate: 0,
          tax_amount: 0,
          amount_no_tax: sanitized.amount,
        }),
    };

    const hasSplits = Array.isArray(payload.project_splits) && payload.project_splits.length > 0;
    if (hasSplits) {
      payload.project = null;
    } else {
      delete payload.project_splits;
    }
    delete payload.split_siblings;
    delete payload.split_master;

    // Editing an existing split child: structural changes go through /resplit/.
    // The regular PATCH must not carry project_splits so it doesn't trip serializer validation.
    if (isEdit && intentData?.split_siblings) {
      delete payload.project_splits;
      delete payload.project;
    }
    return payload;
};

/**
 * Sends a built payload to the endpoint the drawer would pick. Returns
 * `{ kind, request }`; `kind` ('split' | 'wizardMaster' | 'plain') tells the
 * caller which of the drawer's after-save steps apply. `quiet` suppresses the
 * success toast (inline edits show the result in the cell instead).
 *
 * Active-dimension dispatch:
 *   split  + master → POST /update_master/  (this occurrence only)
 *   split  + child  → PATCH + split_choice=single
 *   wizard + all    → PATCH + wizard_choice=all + past flags
 *   wizard + single → PATCH + wizard_choice=single
 */
export const submitIntentSave = (payload, {
  id, isEdit = id != null, intentData, dimension, splitEditMode, wizardEditMode,
  includePastUnpaid, includePastPaid, quiet = false,
}) => {
  const opts = (successMsg) => (quiet ? { successMsg: null } : successMsg === undefined ? undefined : { successMsg });

  if (dimension === 'split' && isEdit && intentData?.split_siblings) {
    const request = splitEditMode === 'master'
      ? api.post(`/api/intents/${id}/update_master/`, payload, opts('SKUPNA transakcija posodobljena'))
      : api.update(`/api/intents/${id}/`, { ...payload, split_choice: 'single' }, opts());
    return { kind: 'split', request };
  }

  if (dimension === 'wizard' && isEdit && intentData?.wizard) {
    payload.wizard_choice = wizardEditMode;
    if (wizardEditMode === 'all') {
      payload.wizard_include_past_unpaid = includePastUnpaid;
      payload.wizard_include_past_paid = includePastPaid;
    }
    // On a split intent, the wizard dimension edits the MASTER amount.
    // Route through update_master so `amount` is treated as the master
    // total (not derived via percent). The wizard params drive
    // cross-occurrence propagation inside update_master.
    if (intentData?.split_siblings) {
      const request = api.post(
        `/api/intents/${id}/update_master/`,
        payload,
        opts(wizardEditMode === 'all'
          ? 'Posodobljene vse ponovitve v dosegu'
          : 'Posodobljena samo ta ponovitev')
      );
      return { kind: 'wizardMaster', request };
    }
  }

  const request = isEdit
    ? api.update(`/api/intents/${id}/`, payload, opts())
    : api.create('/api/intents/', payload, opts());
  return { kind: 'plain', request };
};

/**
 * Which confirmation the drawer puts between Shrani and the save, in the
 * drawer's own order (IntentDrawer.onSubmit): 'split' | 'wizard' | 'budget' | null.
 * 'budget' means "ask /check_budget_sync/ first; show the dialog only if it says so".
 */
export const confirmStepFor = (intentData, dimension) => {
  if (dimension === 'split' && intentData?.split_siblings) return 'split';
  if (dimension === 'wizard' && intentData?.wizard) return 'wizard';
  if (intentData?.proj_budget_expense_plan_row) return 'budget';
  if (intentData?.wizard != null) return 'wizard';
  return null;
};

/** The body for POST /api/intents/{id}/check_budget_sync/. */
export const budgetCheckPayload = (values) => ({
  ...values,
  date_invoice_recieved: fmtDate(values.date_invoice_recieved),
  date_service_performed: fmtDate(values.date_service_performed),
  date_to_be_paid: fmtDate(values.date_to_be_paid),
});

export const checkBudgetSync = (id, values) =>
  api.post(`/api/intents/${id}/check_budget_sync/`, budgetCheckPayload(values), { successMsg: null });


// --- inline cell editing -------------------------------------------------------

/**
 * Net/VAT/gross with the rate held fixed. A gross edit splits the new gross by
 * the rate; a net or rate edit grows the net by the rate. Always leaves
 * amount_no_tax + tax_amount === amount, so the server stores them verbatim.
 */
export const fromGross = (gross, rate) => {
  const n = round2(gross / (1 + rate / 100));
  return { amount: gross, amount_no_tax: n, tax_amount: round2(gross - n) };
};

export const fromNet = (net, rate) => {
  const g = round2(net * (1 + rate / 100));
  return { amount_no_tax: net, amount: g, tax_amount: round2(g - net) };
};

const KIND_BY_FIELD = {
  title: 'text',
  company: 'company',
  person: 'person',
  project: 'project',
  account: 'account',
  tag: 'tag',
  is_paid: 'toggle',
  is_issued: 'toggle',
  date_invoice_recieved: 'date',
  date_service_performed: 'date',
  date_to_be_paid: 'date',
  amount: 'money',
  amount_no_tax: 'money',
  tax_rate: 'rate',
};

/**
 * How a table cell reacts to a double-click:
 *   an editor kind (see KIND_BY_FIELD) -- edit in place;
 *   'drawer' -- the field needs the drawer (its split/transfer handling);
 *   null     -- the drawer can't edit it for this row either.
 */
export const inlineEditKind = (intent, field, orgConfig) => {
  if (field === 'type') return 'drawer';
  // A split row's amounts are the group total in the drawer, and its project
  // is the split itself -- neither maps onto the one child cell shown here.
  if (isSplitRow(intent) && ['amount', 'amount_no_tax', 'tax_rate', 'project'].includes(field)) return 'drawer';
  // A transfer is two signed legs moved by transfer_from/to_account.
  if (intent.type === 'TRANSFER' && ['amount', 'amount_no_tax', 'tax_rate', 'account'].includes(field)) return 'drawer';
  // DDV rows: only the gross is open in the drawer (net and rate are derived).
  if (intent.type === 'TAX' && ['amount_no_tax', 'tax_rate'].includes(field)) return null;
  if (!drawerFieldNames(intent, orgConfig).includes(field)) return null;
  return KIND_BY_FIELD[field] ?? null;
};

/**
 * The drawer-form values for saving `intent` with one field changed: what the
 * drawer would hold after loading the row and the user editing that field.
 * Amount edits re-derive their siblings with the rate held fixed.
 */
export const inlineFormValues = (intent, field, value, orgConfig) => {
  const names = drawerFieldNames(intent, orgConfig);
  const loaded = formValuesFromIntent(intent);
  const values = Object.fromEntries(names.map(n => [n, loaded[n]]));

  // The drawer zeroes the rate on a transfer as soon as it loads one.
  if (intent.type === 'TRANSFER' && names.includes('tax_rate')) values.tax_rate = 0;

  values[field] = DATE_FIELDS.includes(field) ? toDayjs(value) : value;

  if (orgConfig.tax_enabled && AMOUNT_FIELDS.includes(field)) {
    const rate = num(values.tax_rate) ?? 0;
    Object.assign(values, field === 'amount'
      ? fromGross(num(values.amount), rate)
      : fromNet(num(values.amount_no_tax), rate));
  }

  // Derived fields the drawer does not send for this row (e.g. a DDV row's tax_amount).
  return Object.fromEntries(Object.entries(values).filter(([k]) => names.includes(k)));
};
